import { useEffect, useId, useRef, useState } from "react";
import { Play, Pause, FileText, ArrowUpRight, Film } from "lucide-react";

const PLAY_EVENT = "katlas:showcase-play";

const DEPLOYMENT_LABELS = {
  showcase: "Showcase",
  "live-katlas-node": "Live · KATLAS node",
  simulation: "Simulation",
  concept: "Concept",
  archived: "Archived",
};

// Calm, flat schematic used when a poster is missing or the animation is not
// yet supplied. No gradients, no generic network graphics — just brand hairlines.
function DesignedPlaceholder({ sector, note }) {
  return (
    <div className="absolute inset-0 bg-ink" aria-hidden="true">
      <div
        className="absolute inset-0 opacity-[0.14]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(22,127,120,0.9) 1px, transparent 1px), linear-gradient(to bottom, rgba(22,127,120,0.9) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
        <span className="flex h-11 w-11 items-center justify-center border border-stone/25">
          <Film className="h-5 w-5 text-stone/60" strokeWidth={1.5} />
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-stone/55">
          {note}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone/35">
          {sector}
        </span>
      </div>
    </div>
  );
}

/**
 * Reusable, registry-driven showcase.
 * Explains the Control Decision Envelope as an operational sequence — not a
 * dashboard recording. Remains fully functional when the MP4/poster are absent.
 */
export default function ShowcaseVideo({ item, featured = false, className = "" }) {
  const {
    id,
    kind = "preview",
    title,
    caption,
    videoUrl,
    posterUrl,
    captionsUrl,
    destinationUrl,
    mediaStatus = "awaiting-media",
    deploymentStatus = "concept",
    sector,
    transcript = [],
    proposition,
    outcomes = [],
  } = item;

  const videoRef = useRef(null);
  const uid = useId();
  const [playing, setPlaying] = useState(false);
  const [mounted, setMounted] = useState(false); // has the <video> been requested
  const [videoError, setVideoError] = useState(false);
  const [posterError, setPosterError] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  const isPlaceholder = kind === "placeholder";
  const mediaApproved = mediaStatus === "approved";
  const hasMedia = Boolean(videoUrl) && mediaApproved;
  const canPlay = hasMedia && !isPlaceholder && !videoError;
  // Show a real poster/plate whenever one is supplied (even for concept/awaiting-media,
  // e.g. the Logistics concept plate). Play stays gated on approved media via canPlay.
  const showingPoster = Boolean(posterUrl) && !posterError && !isPlaceholder;

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  // Ensure only one showcase plays at a time.
  useEffect(() => {
    const onOtherPlay = (e) => {
      if (e.detail !== id && videoRef.current) {
        videoRef.current.pause();
        setPlaying(false);
      }
    };
    window.addEventListener(PLAY_EVENT, onOtherPlay);
    return () => window.removeEventListener(PLAY_EVENT, onOtherPlay);
  }, [id]);

  const startPlay = () => {
    if (!canPlay) return;
    window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: id }));
    setMounted(true);
    setPlaying(true);
    // If the <video> is already mounted, resume it.
    if (videoRef.current) {
      videoRef.current.play?.().catch(() => {});
    }
  };

  const pausePlay = () => {
    videoRef.current?.pause();
    setPlaying(false);
  };

  const togglePlay = () => (playing ? pausePlay() : startPlay());

  const liveStatus = deploymentStatus === "live-katlas-node";
  const deploymentLabel = DEPLOYMENT_LABELS[deploymentStatus] || deploymentStatus;

  return (
    <figure
      className={`flex flex-col border border-ink/15 bg-stone ${className}`}
      data-testid={`showcase-${id}`}
    >
      {/* Media */}
      <div
        className={`relative w-full overflow-hidden bg-ink ${featured ? "aspect-video" : "aspect-[16/10]"}`}
      >
        {mounted && canPlay ? (
          <video
            ref={videoRef}
            className="absolute inset-0 h-full w-full object-cover"
            src={videoUrl}
            poster={!posterError ? posterUrl : undefined}
            controls
            playsInline
            autoPlay={!reducedMotion}
            preload="none"
            crossOrigin="anonymous"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            onError={() => {
              setVideoError(true);
              setPlaying(false);
              setMounted(false);
            }}
            data-testid={`showcase-video-${id}`}
          >
            {captionsUrl && (
              <track
                kind="captions"
                src={captionsUrl}
                srcLang="en"
                label="English"
                default
              />
            )}
          </video>
        ) : (
          <>
            {showingPoster ? (
              <img
                src={posterUrl}
                alt={`${title} — poster`}
                className="absolute inset-0 h-full w-full object-cover"
                loading="lazy"
                onError={() => setPosterError(true)}
                data-testid={`showcase-poster-${id}`}
              />
            ) : (
              <DesignedPlaceholder
                sector={sector}
                note={
                  isPlaceholder
                    ? "Awaiting validated animation"
                    : mediaStatus === "awaiting-media"
                    ? "Awaiting approved media"
                    : "Media not yet supplied"
                }
              />
            )}

            {/* Flat scrim only when a placeholder is shown; real plates render at full brightness. */}
            {!showingPoster && <div className="absolute inset-0 bg-ink/25" aria-hidden="true" />}

            {/* Labels */}
            <div className="absolute left-4 top-4 flex flex-wrap gap-2">
              {deploymentStatus && (
                <span className={`border bg-ink/60 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${liveStatus ? "border-teal/50 text-teal" : "border-stone/25 text-stone/80"}`}>
                  {deploymentLabel}
                </span>
              )}
              {sector && (
                <span className="border border-stone/25 bg-ink/60 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-stone/70">
                  {sector}
                </span>
              )}
            </div>

            {/* Play affordance / pending chip */}
            {canPlay ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <button
                  type="button"
                  onClick={startPlay}
                  aria-label={`Play ${title}`}
                  data-testid={`showcase-play-${id}`}
                  className="group inline-flex items-center gap-3 border border-stone/40 bg-ink/70 px-5 py-3 font-sans text-sm text-stone backdrop-blur-[1px] transition-colors duration-300 hover:border-stone hover:bg-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-teal"
                >
                  <Play className="h-4 w-4 fill-stone" strokeWidth={0} />
                  Play sequence
                </button>
              </div>
            ) : (
              <span
                className="absolute bottom-4 right-4 border border-stone/25 bg-ink/60 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-stone/70"
                data-testid={`showcase-pending-${id}`}
              >
                {isPlaceholder
                  ? "Media pending"
                  : mediaStatus === "awaiting-media"
                  ? "Awaiting media"
                  : "Animation pending"}
              </span>
            )}
          </>
        )}
      </div>

      {/* Meta */}
      <figcaption className="flex flex-1 flex-col p-6 md:p-7">
        <h3 className={`font-serif text-ink ${featured ? "text-2xl md:text-3xl" : "text-xl"}`}>
          {title}
        </h3>
        <p className="mt-3 flex-1 font-sans text-sm leading-relaxed text-ink/70">{caption}</p>

        {featured && proposition && (
          <p className="mt-5 border-l-2 border-teal pl-4 font-sans text-sm italic leading-relaxed text-ink/75">
            {proposition}
          </p>
        )}

        {/* Controls row */}
        <div className="mt-6 flex flex-wrap items-center gap-4">
          {canPlay && (
            <button
              type="button"
              onClick={togglePlay}
              className="inline-flex items-center gap-2 font-sans text-sm text-ink/75 transition-colors hover:text-teal focus:outline-none focus-visible:ring-2 focus-visible:ring-teal"
              data-testid={`showcase-toggle-${id}`}
              aria-pressed={playing}
            >
              {playing ? <Pause className="h-4 w-4" strokeWidth={1.75} /> : <Play className="h-4 w-4" strokeWidth={1.75} />}
              {playing ? "Pause" : "Play"}
            </button>
          )}

          <button
            type="button"
            onClick={() => setShowTranscript((v) => !v)}
            aria-expanded={showTranscript}
            aria-controls={`transcript-${uid}`}
            data-testid={`showcase-transcript-toggle-${id}`}
            className="inline-flex items-center gap-2 font-sans text-sm text-ink/75 transition-colors hover:text-teal focus:outline-none focus-visible:ring-2 focus-visible:ring-teal"
          >
            <FileText className="h-4 w-4" strokeWidth={1.75} />
            {showTranscript ? "Hide text equivalent" : "Text equivalent"}
          </button>

          {destinationUrl && (
            <a
              href={destinationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-1.5 font-sans text-sm text-ink/75 transition-colors hover:text-teal"
              data-testid={`showcase-destination-${id}`}
            >
              Open demonstrator
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.75} />
            </a>
          )}
        </div>

        {/* Text equivalent / transcript */}
        {showTranscript && (
          <div
            id={`transcript-${uid}`}
            role="region"
            aria-label={`${title} — operational sequence, text equivalent`}
            className="mt-6 border-t border-ink/12 pt-6"
            data-testid={`showcase-transcript-${id}`}
          >
            {proposition && !featured && (
              <p className="mb-4 font-sans text-sm italic leading-relaxed text-ink/70">{proposition}</p>
            )}
            <ol className="space-y-2">
              {transcript.map((step, i) => (
                <li key={i} className="flex gap-4 font-sans text-sm text-ink/75">
                  <span className="font-mono text-xs text-teal">{`0${i + 1}`}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            {outcomes.length > 0 && (
              <div className="mt-5">
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/45">
                  Envelope outcome
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {outcomes.map((o) => (
                    <span key={o} className="border border-ink/25 px-3 py-1 font-mono text-xs text-ink/75">
                      {o}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </figcaption>
    </figure>
  );
}
